import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import {
  getPrevNextKeyUsingRef,
  getFirstVisibleKey,
  getKey,
  keyExtractor,
  findElementByKey,
  getItemKeysArray
} from '../../utils/PRIVATE_collectionUtils';
import { useCollectionFocusRing } from '../PRIVATE_useCollectionFocusRing';
import { useCurrentKey } from '../PRIVATE_useCurrentKey';
import { useId } from '../UNSAFE_useId';
import type { ComponentProps, ContextType, RefObject } from 'preact';
import type { TabBarContext, TabBarLayout } from '../../UNSAFE_TabBarCommon';
import { useReorderableContext } from '../../hooks/PRIVATE_useReorderable/useReorderableContext';
import { Menu } from '../../UNSAFE_Menu';
import { TabBarContextMenuConfig } from '../../UNSAFE_TabBarCommon';
import { useContextMenuGesture } from '../../hooks/UNSAFE_useContextMenuGesture';
import { getDefaultContextMenu } from '../../UNSAFE_TabBar/TabBarContextMenu';
import { TabBarItemContextMenuContext } from '../../UNSAFE_TabBarCommon/TabBar.types';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { useTranslationBundle } from '../../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../../resources/nls/bundle';

type TabBarContextValue = ContextType<typeof TabBarContext>;

export type useTabBarOptions<K extends string | number> = {
  class?: string;
  display?: TabBarContextValue['display'];
  edge?: 'bottom' | 'top';
  onRemove?: TabBarContextValue['onRemove'];
  onSelect?: TabBarContextValue['onSelect'];
  ref?: RefObject<HTMLDivElement>;
  selection?: TabBarContextValue['selection'];
  size?: TabBarContextValue['size'];
  children?: ComponentProps<typeof TabBarLayout>['children'];
  contextMenuConfig?: TabBarContextMenuConfig<K>;
};

const ITEM_SELECTOR = '[role="tab"]';
const REMOVABLE_ICON_SELECTOR = '[data-oj-tabbar-item-remove-icon="true"]';
export const REMOVABLE_ITEM_ATTRIBUTE = 'data-oj-removable';

type MenuProps = Pick<
  ComponentProps<typeof Menu>,
  'anchorRef' | 'initialFocus' | 'placement' | 'offsetValue' | 'isOpen'
>;

const TOUCH_OFFSET_VALUE = 40;

const menuPropGestureStates: Record<
  'mouse' | 'keyboard' | 'touch',
  Omit<Required<MenuProps>, 'anchorRef' | 'isOpen'>
> = {
  mouse: {
    initialFocus: 'menu',
    placement: 'bottom-start',
    offsetValue: 0
  },
  keyboard: {
    initialFocus: 'firstItem',
    placement: 'bottom-start',
    offsetValue: 0
  },
  touch: {
    initialFocus: 'menu',
    placement: 'end',
    offsetValue: TOUCH_OFFSET_VALUE
  }
};

const initialMenuProps: MenuProps = {
  isOpen: false,
  initialFocus: 'menu',
  placement: 'bottom-start',
  offsetValue: 0,
  anchorRef: { current: null }
};

/**
 * Implements TabBar behavior for focus and keyboad handling
 */
export function useTabBar<K extends string | number>(options: useTabBarOptions<K>) {
  const {
    children,
    class: className,
    display,
    edge = 'top',
    selection,
    onSelect,
    onRemove,
    ref: userRef,
    size,
    contextMenuConfig
  } = options;

  const { direction } = useUser();
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const internalRef = useRef<HTMLDivElement>(null);
  const rootRef = userRef || internalRef;

  const tabId = useId();
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection as K);
  const [hideTooltip, setHideTooltip] = useState(false);

  const tabItemPrefix = tabId + '_';

  const contextMenuDescriptionId = 'contextMenu_' + useId();
  const contextMenuDescription = translations.tabbar_labelContextMenu();

  /**
   * A reference to the previous set of tab keys that this TabBar
   * contains before children were re-rendered. In the event the "current" tab
   * is removed and the "currentKey" reference is broken, we can redirect
   * the "currentKey" to another tab based on the index of the removed tab.
   */
  const prevTabKeys = useRef<(string | number | undefined)[]>();

  useEffect(() => {
    if (rootRef.current) {
      const tabKeys = getItemKeysArray(rootRef.current, ITEM_SELECTOR);
      if (currentKey == null && tabKeys) {
        if (selection != null && tabKeys.includes(selection as K)) {
          setCurrentKey(selection as K);
        } else if (tabKeys.length > 0) {
          setCurrentKey(tabKeys[0] as K);
        }
      }
      if (currentKey != null && tabKeys && !tabKeys.includes(currentKey as K)) {
        //If prevTabs does not exist set the first visible key as currentKey
        prevTabKeys.current
          ? setCurrentKey(findNextValidKey(currentKey, tabKeys, prevTabKeys.current) as K)
          : setCurrentKey(tabKeys[0] as K);
      }
      prevTabKeys.current = tabKeys as K[];
    }
  }, [children, currentKey, rootRef, selection]);

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, [
    'ArrowRight',
    'ArrowLeft',
    'Home',
    'End'
  ]);

  const reorderableContext = useReorderableContext();
  const { currentKeyProps } = useCurrentKey(
    (element) =>
      onRemove
        ? extractOnlyItemKey(element, ITEM_SELECTOR, REMOVABLE_ICON_SELECTOR)
        : keyExtractor(element, ITEM_SELECTOR),
    false,
    false,
    undefined,
    undefined,
    getPrevNextKeyUsingRef(rootRef, currentKey, true, ITEM_SELECTOR),
    getPrevNextKeyUsingRef(rootRef, currentKey, false, ITEM_SELECTOR),
    currentKey,
    (detail) => {
      const key = detail.value as K;
      setCurrentKey(key);
      setHideTooltip(false);
    }
  );

  useEffect(() => {
    if (reorderableContext) {
      reorderableContext.currentItemKey.current = currentKey;
    }
  }, [currentKey, reorderableContext]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (rootRef.current && currentKey != null) {
      if (event.key === 'Home' || event.key === 'End') {
        const tabBarItemKey = getItemKeysArray(rootRef.current, ITEM_SELECTOR);
        event.preventDefault();
        setCurrentKey?.(tabBarItemKey[event.key === 'Home' ? 0 : tabBarItemKey.length - 1] as K);
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect?.({ value: currentKey });
      }
      if (event.key === 'Delete') {
        const tabBarItem = findElementByKey(
          rootRef.current,
          currentKey,
          ITEM_SELECTOR
        ) as HTMLElement;
        if (tabBarItem.hasAttribute(REMOVABLE_ITEM_ATTRIBUTE)) {
          onRemove?.({ value: currentKey });
        }
      }
      if (event.key === 'Escape') {
        setHideTooltip(true);
      }
    }
  };

  const onFocus = () => {
    if (rootRef.current && currentKey === undefined) {
      const key = getFirstVisibleKey(rootRef.current, ITEM_SELECTOR);
      if (key != null) {
        setCurrentKey(key as K);
      }
    }
  };

  const [contextMenuContext, setContextMenuContext] = useState<TabBarItemContextMenuContext<K>>();

  const [menuProps, setMenuProps] = useState<MenuProps>(initialMenuProps);
  const [accStatusInfo, setAccStatusInfo] = useState<string>();

  const { triggerProps } = useContextMenuGesture(
    ({ gesture, anchor, target }) => {
      let anchorBasedOnGesture = anchor;
      let itemKey = currentKey as K;
      if (gesture === 'keyboard') {
        //We find the element where the position of the menu is going to be based of
        const elem = findElementByKey(
          rootRef.current as HTMLElement,
          currentKey as K,
          ITEM_SELECTOR
        );
        //We set the correct anchor
        anchorBasedOnGesture = elem ? elem : anchor;
      } else {
        //We search for the key using the target of the event
        const key = keyExtractor(target as HTMLElement, ITEM_SELECTOR);

        //On touch devices, the keyExtractor returns null because target of touch event is the entire tabbar. Hence we make use of currentkey since the context menu is always launched from selected item and its key is currentKey
        itemKey = key != null ? (key as K) : (currentKey as K);
      }
      let tabKeysArray;
      let isRemovableItem = false;
      const isReorderable = reorderableContext?.onReorder != null;
      if (rootRef.current) {
        tabKeysArray = isReorderable
          ? (getItemKeysArray(rootRef.current, ITEM_SELECTOR) as K[])
          : undefined;
        isRemovableItem =
          getItemAttribute(itemKey, rootRef.current, REMOVABLE_ITEM_ATTRIBUTE) != null;
      }
      const isRemovable = onRemove != null && isRemovableItem;
      const defaultContext = getDefaultContextMenu<K>({
        // For default menu items the existence of REMOVABLE_ICON_SELECTOR is the indicator that it is removable because we render the default menu in touch devices not the application.
        // Furthermore in corepack due to delegations onRemove is always defined as it is a even handler hence not a good check for isRemovable.
        isRemovable: isRemovable,
        handleRemove: onRemove,
        handleReorder: isReorderable ? reorderableContext.onReorder : undefined,
        itemKey: itemKey,
        tabKeys: tabKeysArray,
        direction: direction,
        itemLabels: {
          labelRemove: isRemovable ? translations.tabbar_labelRemove() : undefined,
          labelMoveLeft: isReorderable ? translations.tabbar_labelMoveLeft() : undefined,
          labelMoveRight: isReorderable ? translations.tabbar_labelMoveRight() : undefined
        },
        handleAccStatus: (detail) => {
          const removeItemLabel = getItemLabel(detail.removedKey!, rootRef.current!) as string;
          const removeStatus =
            isRemovable && detail.removedKey
              ? translations.tabbar_accAnnounceRemovedKey({ itemKey: removeItemLabel })
              : '';
          const destinationItemLabel = getItemLabel(
            detail.destinationKey!,
            rootRef.current!
          ) as string;
          const reorderStatus =
            isReorderable && detail.destinationKey
              ? translations.accAnnounceMovedKey({
                  // This acc info is for reordering using context menu hence as per spec source item key is not required.
                  itemKey: '',
                  position: detail.position!,
                  referenceKey: destinationItemLabel
                })
              : '';
          setAccStatusInfo(removeStatus + reorderStatus);
        }
      });
      setContextMenuContext(defaultContext);

      setMenuProps({
        ...menuPropGestureStates[gesture],
        anchorRef: { current: anchorBasedOnGesture },
        // This is set so that when no contextMenuConfig is available we need not open it. Example: Apps may choose to open only for removable items and return contextMenuConfig as undefined for non removable ones in such cases it will prevent it from opening.
        isOpen: contextMenuConfig != null
      });
    },
    {
      isDisabled: !contextMenuConfig //If there is no a context menu renderer we disable the hook
    }
  );

  const handleCloseContextMenu = useCallback<Required<ComponentProps<typeof Menu>>['onClose']>(
    (detail) => {
      //Focus has to be set on the current target that was obtained
      //during context menu gesture when menu is dismissed or item is "selected"
      if (detail.reason === 'dismissed' || detail.reason === 'itemAction') {
        setCurrentKey(contextMenuContext?.itemKey);
        rootRef.current?.focus();
      }
      // We close the menu.The only prop that matters here is isOpen
      setMenuProps({ ...initialMenuProps });
    },
    [rootRef, contextMenuContext?.itemKey]
  );

  const eventProps = mergeProps({ onKeyDown, onFocus }, triggerProps);

  return {
    rootProps: mergeProps(
      {
        'aria-activedescendant': currentKey ? tabItemPrefix + currentKey : '',
        'aria-multiselectable': false,
        class: className,
        ref: rootRef,
        role: 'tablist',
        tabIndex: 0
      },
      currentKeyProps,
      focusRingProps,
      eventProps
    ),
    tabBarContext: {
      currentKey,
      display,
      isEdgeBottom: edge === 'bottom',
      layout: 'condense',
      onRemove,
      onSelect,
      showFocusRing,
      hideTooltip,
      selection,
      size,
      tabItemPrefix
    },
    menuProps: { ...menuProps, onClose: handleCloseContextMenu },
    contextMenuContext,
    contextMenuDescriptionId,
    contextMenuDescription,
    accStatusInfo
  } as const;
}

//useCurrentKey use click capture that captures first click, which is on remove button
//when we remove and sets the item being removed to currentKey. So we should not allow
//this if click is on remove button
const extractOnlyItemKey = (element: HTMLElement, itemSelector: string, itemEliminator: string) => {
  const tabBarItem = element.closest(itemSelector);
  if (tabBarItem) {
    if (tabBarItem.hasAttribute(REMOVABLE_ITEM_ATTRIBUTE)) {
      const removeButton = element.closest(itemEliminator);
      if (removeButton && tabBarItem?.contains(removeButton)) {
        return null;
      }
    }
    return getKey(tabBarItem as HTMLElement);
  }
  return null;
};

const findNextValidKey = <K>(currentKey: K, currTabs: K[], prevTabs: K[]) => {
  const index = prevTabs.indexOf(currentKey);
  // update current key to be the first one if currentKey is invalid
  if (index === -1) {
    return currTabs[0];
  }
  let nextIndex = 0;
  let isLastKey = false;
  if (index === prevTabs.length - 1) {
    nextIndex = index - 1;
    isLastKey = true;
  } else {
    nextIndex = index + 1;
  }
  while (nextIndex !== index && nextIndex > -1 && nextIndex < prevTabs.length) {
    const nextKey = prevTabs[nextIndex];
    if (currTabs.indexOf(nextKey) !== -1) {
      return nextKey;
    }
    isLastKey ? nextIndex-- : nextIndex++;
  }
  // update current key to be the first one if we can't find a suitable next key
  return currTabs[0];
};

// Utility that returns the value for a attribute related to the role="tab" item.
export const getItemAttribute = <K extends string | number>(
  itemKey: K,
  root: HTMLElement,
  attribute: string
) => {
  const tabBarItem = findElementByKey(root, itemKey as K, ITEM_SELECTOR) as HTMLElement;
  if (tabBarItem) {
    const tabBarItemAttribute = tabBarItem.hasAttribute(attribute)
      ? tabBarItem.getAttribute(attribute)
      : null;
    return tabBarItemAttribute;
  }
  return;
};

// Utility that returns the visible inner text inside the role="tab" item.
const getItemLabel = <K extends string | number>(itemKey: K, root: HTMLElement) => {
  const tabBarItem = findElementByKey(root, itemKey as K, ITEM_SELECTOR) as HTMLElement;
  if (tabBarItem) {
    return tabBarItem.innerText;
  }
  return;
};
